import { Assert } from "../../Assert";
import { PartialNullable, RadPromiseLike } from "../../Extensions";
import { PathHelper } from "../../Helpers/PathHelper";
import { IPromisePublic, PromiseHelper } from "../../Helpers/PromiseHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$ } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyInt32, PropertyObject, PropertyString } from "../../RadObject";
import { Button } from "../Controls/Button";
import { ContainerControl, ContainerControlPropertyValues, IContainerControl } from "../Controls/ContainerControl";
import { ControlRef } from "../Controls/ControlRef";
import { IPageDimmer, PageDimmer } from "../Controls/PageDimmer";
import { PageFooter } from "../Controls/PageFooter";
import { DialogResultEnum } from "../Dialogs/DialogResultEnum";
import { IDialogResponse } from "../Dialogs/IDialogResponse";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { PageManager } from "../PageManager";
import { RadHistory } from "../RadHistory";
import { NAMESPACE } from "./_Namespace";

import "./Page.scss";

export interface IPageActivatedArgs
{
    promises: RadPromiseLike<any>[]
};

export interface IPageLoadedArgs
{
    promises: RadPromiseLike<any>[]
};

export type PagePropertyValues =
    ContainerControlPropertyValues
    & {
        active: boolean;
        activated: boolean;
        cacheExpiry: number;
        dialogAnimationIn: string;
        dialogAnimationOut: string;
        dialogView: boolean;
        loaded: boolean;
        navigationData: any;
        noCache: boolean;
        requiresAuthentication: boolean;
        scrollable: boolean;
        showPageFooter: boolean;
        showPageHeader: boolean;
        title: string;
        transitionType: string;
        transitionName: string;
        url: string;
        urlParameters: { [key: string]: string };
    };

export type PageProperties = {
    [name in keyof PagePropertyValues]: Property<PagePropertyValues[name]>
};

export interface IPage extends IContainerControl, PageProperties
{
    activateAsync(): Promise<void>;
    closeAsync(): RadPromiseLike<any>;
    deactivateAsync(): Promise<void>;
    loadAsync(): Promise<void>;
    openAsDialogAsync(): RadPromiseLike<IDialogResponse>;
    reset(): void;
    scrollToTop(withoutAnimation?: boolean): void;
    updateNavigationData(navigationData: any): void;
}

export class Page<TPropertyValues extends PagePropertyValues = PagePropertyValues> extends ContainerControl<TPropertyValues> implements IPage
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Page";
    public static override className = "Page";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<PagePropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                activated: false,
                active: false,
                cacheExpiry: -1,
                cssClass: "rad-control-page",
                dialogAnimationIn: "slide left",
                dialogAnimationOut: "slide left",
                dialogView: false,
                isVisible: false,
                loaded: false,
                noCache: false,
                scrollable: true,
                showPageFooter: true,
                showPageHeader: true,
                transitionName: "bouncy",
                transitionType: "animation",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _containerInnerWrapperElementRef!: ElementRef;
    protected _containerWrapperElementRef!: ElementRef;
    protected _headerElementRef!: ElementRef;
    public _pageFooterControlRef!: ControlRef<PageFooter>;
    protected _titleElementRef!: ElementRef;
    protected _titleTextElementRef!: ElementRef;

    protected _blockNavigationFunction: () => boolean;
    protected cacheExpiryTimeoutId?: number;
    protected closePromise?: IPromisePublic<IDialogResponse>;
    protected dialogDimmer?: IPageDimmer;
    public dialogResult?: DialogResultEnum;
    protected inhibitScrollToTopOnReset?: boolean;
    public opened?: boolean;
    public openedAsDialog?: boolean
    protected openPromise?: IPromisePublic<IDialogResponse>;
    public promiseData?: any;

    // #endregion

    // #region Properties

    public active!: PropertyBoolean;
    public activated!: PropertyBoolean;
    public cacheExpiry!: PropertyInt32;
    public dialogAnimationIn!: PropertyString;
    public dialogAnimationOut!: PropertyString;
    public dialogView!: PropertyBoolean;
    public loaded!: PropertyBoolean;
    public navigationData!: PropertyObject;
    public noCache!: PropertyBoolean;
    public requiresAuthentication!: PropertyBoolean;
    public scrollable!: PropertyBoolean;
    public showPageFooter!: PropertyBoolean;
    public showPageHeader!: PropertyBoolean;
    public title!: PropertyString;
    public transitionType!: PropertyString;
    public transitionName!: PropertyString;
    public url!: PropertyString;
    public urlParameters!: Property<{ [key: string]: string }>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TPropertyValues>)
    {
        super(propertyValues);

        this._blockNavigationFunction = () =>
        {
            return !!(this.dialogView.get() && this.dialogDimmer?.shown());
        };
    }

    // #endregion

    // #region Public Methods

    public activateAsync(): Promise<void>
    {
        const
            eventData = { promises: [] };

        this.trigger("beforeActivated", eventData);

        return (
            Promise.all(eventData.promises)
                .then(() =>
                {
                    return this._activateAsync();
                })
                .then(() =>
                {
                    this.activated.set(true);

                    const
                        eventData: IPageActivatedArgs = { promises: [] };

                    this.trigger("activated", eventData);

                    return Promise.all(eventData.promises);
                })
                .then(() =>
                {
                    this.active.set(true);
                })
        );
    }
    public closeAsync(): RadPromiseLike<any>
    {
        if (this.openedAsDialog)
        {
            if (this.closePromise)
                return this.closePromise;

            this.closePromise = PromiseHelper.createPromisePublic();

            const
                promise = this.closePromise;

            this.removeCssClass("visible");

            this._getDialogDimmer()
                .hideElementOrControlAsync(this)
                .then(() =>
                {
                    this.opened = false;

                    if (this.openPromise)
                        this.openPromise.resolve({ dialogResult: this.dialogResult ?? DialogResultEnum.none, promiseData: this.promiseData });

                    this.closePromise?.resolve({ dialogResult: this.dialogResult ?? DialogResultEnum.none, promiseData: this.promiseData });
                    delete this.closePromise;

                    PageManager.instance?.unRegisterBlockNavigationFunction(this._blockNavigationFunction);
                });

            return promise;
        }

        if (RadHistory.pushStateCounter > 0)
        {
            return new Promise(resolve =>
            {
                window.history.back();
                setTimeout(() => { resolve(undefined); });
            });
        }

        const
            location = PathHelper.getLocation(this.url.get() ?? StringHelper.empty),
            pathNameParts = StringHelper.trim(location.pathname, "/").split("/");

        pathNameParts.pop();

        Assert.isNotNullOrUndefined(PageManager.instance, "PageManager.instance");

        return PageManager.instance.navigateAsync(StringHelper.format("/{0}{1}", pathNameParts.join("/"), pathNameParts.length ? "/" : StringHelper.empty), true, true);
    }
    public deactivateAsync(): Promise<void>
    {
        const
            eventData = { promises: [] };

        this.trigger("beforeDeactivated", eventData);

        return (
            Promise.all(eventData.promises)
                .then(() =>
                {
                    return this._deactivateAsync();
                })
                .then(() =>
                {
                    const
                        eventData = { promises: [] };

                    this.trigger("deactivated", eventData);

                    return Promise.all(eventData.promises);
                })
                .then(() =>
                {
                    this.active.set(false);
                })
        );
    }
    public loadAsync(): Promise<void>
    {
        const
            eventData = { promises: [] };

        this.trigger("beforeLoaded", eventData);

        return (
            Promise.all(eventData.promises)
                .then(() =>
                {
                    return this._loadAsync();
                })
                .then(() =>
                {
                    this.loaded.set(true);

                    const
                        eventData: IPageLoadedArgs = { promises: [] };

                    this.trigger("loaded", eventData);
                })
        );
    }
    public openAsDialogAsync(): RadPromiseLike<IDialogResponse>
    {
        if (this.opened && this.openPromise)
            return this.openPromise;

        this.dialogView.set(true);

        this.opened = true;

        this.openPromise = PromiseHelper.createPromisePublic<IDialogResponse>();

        this.loadAsync()
            .then(() =>
            {
                return this._getDialogDimmer().showElementOrControlAsync(this);
            })
            .then(() =>
            {
                PageManager.instance?.registerBlockNavigationFunction(this._blockNavigationFunction);
                this.focus();
            })
            .catch(error =>
            {
                this.openPromise?.reject(error);
                this.closeAsync();
            });

        return this.openPromise;
    }
    public reset(): void
    {
        if (this.inhibitScrollToTopOnReset)
            return;

        const
            containerWrapper = this._containerWrapperElementRef.current.get(0);

        if (containerWrapper instanceof HTMLElement)
            containerWrapper.scrollTop = 0;
    }
    public scrollToTop(withoutAnimation?: boolean): void
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        this._containerWrapperElementRef.current
            .stop()
            .animate({
                scrollTop: $$element.position().top
            }, withoutAnimation ? 0 : 500);
    }
    public updateNavigationData(navigationData: any): void
    {
        const
            currentPage = PageManager.instance?.currentPage.get();

        if (!currentPage || currentPage !== this)
            throw new Error("Navigation data can only be updated by the current page");

        this.navigationData.set(navigationData);
        window.history.replaceState({ counter: RadHistory.pushStateCounter, navigationData: navigationData }, StringHelper.empty, this.url.get());
    }

    // #endregion

    // #region Protected Methods

    protected _activateAsync(): RadPromiseLike<any>
    {
        return Promise.resolve();
    }
    protected _activatedChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _activeChanged(change: PropertyChange<boolean>)
    {
        this.triggerHandler("activeChanged", change);
    }
    protected _cacheExpiryChanged(change: PropertyChange<number>)
    {
        if (!this.cacheExpiryTimeoutId)
        {
            window.clearTimeout(this.cacheExpiryTimeoutId);
            this.cacheExpiryTimeoutId = 0;
        }

        if (change.currentValue === null || change.currentValue < -1)
            throw new Error("Cache expiry value must be a positive number or -1 for no aging.");

        if (change.currentValue === -1 || this.noCache.get())
            return;

        if (change.currentValue === 0)
        {
            this.noCache.set(true);
            return;
        }

        this.cacheExpiryTimeoutId = window.setTimeout(() =>
        {
            if (this.isVisible.get())
                this.noCache.set(true);
            else
                this.remove();
        }, change.currentValue * 1000);
    }
    protected _deactivateAsync()
    {
        return Promise.resolve();
    }
    protected _dialogAnimationInChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _dialogAnimationOutChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _dialogViewChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
        {
            this.openedAsDialog = true;
            this.addCssClass("rad-control-dialog");
            this._headerElementRef.current.prependTo(this.$$element.get());
            this._headerElementRef.current.find(">h2").removeClass("dividing");
            this._containerWrapperElementRef.current.addClass("content ui form");
            this._pageFooterControlRef.current?.addCssClass("rad-dialog-footer");
            this._pageFooterControlRef.current?.removeScrollbarPadding();
        }
    }
    protected _displayErrorDialogAsync(message: string): Promise<{ dialogResult: DialogResultEnum; }>
    {
        if (StringHelper.isNullOrWhitespace(message))
            message = "An error occurred";

        return new Promise<{ dialogResult: DialogResultEnum }>(resolve =>
        {
            $$(document).triggerHandler(
                "Error",
                {
                    message: message,
                    promise:
                        PromiseHelper.createPromisePublic()
                            .finally(() => { resolve({ dialogResult: DialogResultEnum.ok }); })
                }
            );
        });
    }
    protected _getDialogDimmer(): IPageDimmer
    {
        if (!this.dialogDimmer)
            this.dialogDimmer = new PageDimmer({ animationIn: this.dialogAnimationIn.get(), animationOut: this.dialogAnimationOut.get(), cssClass: "rad-dialog-dimmer" });

        return this.dialogDimmer;
    }
    protected _loadAsync(): RadPromiseLike<any>
    {
        return Promise.resolve();
    }
    protected _loadedChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _navigationDataChanged()
    {
        /* Intentionally empty */
    }
    protected _noCacheChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _requiresAuthenticationChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _scrollableChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-feature-scrollable");
        else
            this.removeCssClass("rad-feature-scrollable");
    }
    protected _showPageFooterChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
        {
            this.addCssClass("rad-state-show-page-footer");
            this._pageFooterControlRef.current?.show();
        }
        else
        {
            this.removeCssClass("rad-state-show-page-footer");
            this._pageFooterControlRef.current?.hide();
        }
    }
    protected _showPageHeaderChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
        {
            this.addCssClass("rad-state-show-page-header");
            this._headerElementRef.current.show();
        }
        else
        {
            this.removeCssClass("rad-state-show-page-header");
            this._headerElementRef.current.hide();
        }
    }
    protected _titleChanged(change: PropertyChange<string>)
    {
        this._titleTextElementRef.current.text(change.currentValue);
    }
    protected _transitionNameChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _transitionTypeChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _urlChanged(change: PropertyChange<string>)
    {
        if (change.currentValue)
            this.$$element.get()?.attr("data-url", change.currentValue);

        if (!this.openedAsDialog && PageManager.instance?.currentPage.get() === this && change.currentValue)
            PageManager.instance.replaceUrl(change.currentValue);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._containerInnerWrapperElementRef = new ElementRef();
        this._containerWrapperElementRef = new ElementRef();
        this._headerElementRef = new ElementRef();
        this._pageFooterControlRef = new ControlRef();
        this._titleElementRef = new ElementRef();
        this._titleTextElementRef = new ElementRef();

        this.active = new PropertyBoolean(this, propertyValues.active, this._activeChanged);
        this.activated = new PropertyBoolean(this, propertyValues.activated, this._activatedChanged);
        this.cacheExpiry = new PropertyInt32(this, propertyValues.cacheExpiry, this._cacheExpiryChanged);
        this.dialogAnimationIn = new PropertyString(this, propertyValues.dialogAnimationIn, this._dialogAnimationInChanged);
        this.dialogAnimationOut = new PropertyString(this, propertyValues.dialogAnimationOut, this._dialogAnimationOutChanged);
        this.dialogView = new PropertyBoolean(this, propertyValues.dialogView, this._dialogViewChanged);
        this.loaded = new PropertyBoolean(this, propertyValues.loaded, this._loadedChanged);
        this.navigationData = new PropertyObject(this, propertyValues.navigationData, this._navigationDataChanged);
        this.noCache = new PropertyBoolean(this, propertyValues.noCache, this._noCacheChanged);
        this.requiresAuthentication = new PropertyBoolean(this, propertyValues.requiresAuthentication, this._requiresAuthenticationChanged);
        this.scrollable = new PropertyBoolean(this, propertyValues.scrollable, this._scrollableChanged);
        this.showPageFooter = new PropertyBoolean(this, propertyValues.showPageFooter, this._showPageFooterChanged);
        this.showPageHeader = new PropertyBoolean(this, propertyValues.showPageHeader, this._showPageHeaderChanged);
        this.title = new PropertyString(this, propertyValues.title, this._titleChanged);
        this.transitionType = new PropertyString(this, propertyValues.transitionType, this._transitionTypeChanged);
        this.transitionName = new PropertyString(this, propertyValues.transitionName, this._transitionNameChanged);
        this.url = new PropertyString(this, propertyValues.url, this._urlChanged);
        this.urlParameters = new Property<{ [key: string]: string }>(this, "System.Object, System.Private.CoreLib", propertyValues.urlParameters);

        this.url.overrideSet((value, next) =>
        {
            if (value?.startsWith("#"))
                value = window.location.pathname + window.location.search + value;

            // If we change the URL, we need to make sure an existing page with the new Url is removed
            const
                page = value ? PageManager.instance?.getPageByUrl(value) : null;

            if (page && page !== this)
                page.remove();

            next(value);
        });
    }

    protected override _createElement()
    {
        const
            $$element = super._createElement();


        return $$element;
    }
    protected override _createElementComplete()
    {
        super._createElementComplete();

        this.addCssClass("rad-control-page");

        this._headerElementRef.current.addClass("rad-page-header ui container fluid");
        this._containerWrapperElementRef.current.addClass("rad-control-container-wrapper");
        this._containerInnerWrapperElementRef.current.addClass("rad-control-container-inner-wrapper");
        this._containerElementRef.current.addClass("rad-control-container ui container fluid");

        // Trigger Property Changes
        this.cacheExpiry.triggerChange();
        this.scrollable.triggerChange();
        this.showPageFooter.triggerChange();
        this.showPageHeader.triggerChange();
        this.title.triggerChange();
        this.transitionType.triggerChange();
        this.transitionName.triggerChange();
        this.url.triggerChange();
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <div class="rad-control rad-control-container-control rad-control-page">
                <div ref={this._containerWrapperElementRef} class="rad-control-container-wrapper">
                    <div ref={this._containerInnerWrapperElementRef} class="rad-control-container-inner-wrapper">
                        {this._createHeaderHtml()}
                        <div class="rad-control-container ui container fluid">
                            <div ref={this._containerElementRef} class="rad-control-container-inner">
                                {children ?? this._createChildrenHtml()}
                            </div>
                        </div>
                    </div>
                </div>
                {this._createFooterHtml()}
            </div>
        );
    }
    protected _createFooterHtml()
    {
        return (
            <PageFooter ref={this._pageFooterControlRef}>
                <Button text="Close" inline={true} action="closeAsync" actionTargetSelector=".rad-control-page" cssClass="rad-action-close" />
            </PageFooter>
        );
    }
    protected _createHeaderHtml()
    {
        return (
            <header ref={this._headerElementRef} class="rad-page-header ui container fluid">
                <div class="rad-page-header-inner-wrapper">
                    <h1 ref={this._titleElementRef} class="ui dividing header">
                        <span ref={this._titleTextElementRef} class="rad-title"></span>
                    </h1>
                </div>
            </header>
        );
    }
    protected override _dispose()
    {
        this.dialogDimmer?.remove();

        super._dispose();
    }

    public override focus()
    {
        super.focus();

        // PH break out of execution due to safari renderer issue
        setTimeout(() => { this.scrollToTop(true); });
    }
    public override hide()
    {
        super.hide();

        if (this.noCache.get())
        {
            this.remove();
            return this;
        }

        return this;
    }
    public override remove()
    {
        this.removeControls([
            this._pageFooterControlRef.current
        ]);

        super.remove();
    }
    public override show()
    {
        super.show();

        this.trigger("shown");

        return this;
    }

    // #endregion

    // #endregion
}
